import React, { useState, useEffect, useMemo } from "react";
import { vouchersAPI, roomsAPI, activityLogAPI } from "../services/api";
import Pagination from "../components/ui/Pagination";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { Voucher, VoucherFormData } from "../types/voucher";
import { Room } from "../types/room";
import { localDateStr } from "../utils/date";

const PAGE_SIZE = 10;

interface HolderEntry {
  name: string;
  email: string;
  checkInDate?: string;
  checkOutDate?: string;
  createdAt?: string;
}
interface VoucherHolder {
  code: string;
  claimed: HolderEntry[];
  used: HolderEntry[];
}

function VouchersPage(): React.ReactElement {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"vouchers" | "holders">("vouchers");
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [holders, setHolders] = useState<VoucherHolder[]>([]);
  const [holdersLoading, setHoldersLoading] = useState(false);
  const [expandedCode, setExpandedCode] = useState<string | null>(null);

  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState(1);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingVoucher, setEditingVoucher] = useState<Voucher | null>(null);
  const [formData, setFormData] = useState<VoucherFormData>({
    code: "",
    description: "",
    discountType: "percentage",
    discountValue: "",
    minPurchase: "",
    maxDiscount: "",
    usageLimit: "",
    startDate: "",
    endDate: "",
    applicableRooms: [],
  });

  // Fetch vouchers and rooms from API
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === "holders" && holders.length === 0) {
      setHoldersLoading(true);
      vouchersAPI.getHolders()
        .then(res => setHolders(res.data.data || []))
        .catch(() => toast.error("Lỗi tải danh sách người dùng"))
        .finally(() => setHoldersLoading(false));
    }
  }, [activeTab, holders.length]);

  const fetchData = async (): Promise<void> => {
    try {
      const [vouchersRes, roomsRes] = await Promise.all([
        vouchersAPI.getAll(),
        roomsAPI.getAll(),
      ]);
      setVouchers(vouchersRes.data.data || []);
      setRooms(roomsRes.data.data || []);
    } catch (error) {
      toast.error("Lỗi khi tải dữ liệu");
    } finally {
      setLoading(false);
    }
  };

  const fetchVouchers = async (): Promise<void> => {
    try {
      const response = await vouchersAPI.getAll();
      setVouchers(response.data.data || []);
    } catch (error) {
      toast.error("Lỗi khi tải danh sách voucher");
    }
  };

  const filteredVouchers = useMemo(() => {
    if (!search.trim()) return vouchers;
    const q = search.trim().toLowerCase();
    return vouchers.filter((v) =>
      v.code.toLowerCase().includes(q) || (v.description || "").toLowerCase().includes(q)
    );
  }, [vouchers, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.ceil(filteredVouchers.length / PAGE_SIZE);
  const pageVouchers = filteredVouchers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Open modal for adding new voucher
  const handleAddNew = (): void => {
    setEditingVoucher(null);
    setFormData({
      code: "",
      description: "",
      discountType: "percentage",
      discountValue: "",
      minPurchase: "",
      maxDiscount: "",
      usageLimit: "",
      startDate: localDateStr(),
      endDate: "",
      applicableRooms: [],
    });
    setShowModal(true);
  };

  // Open modal for editing
  const handleEdit = (voucher: Voucher): void => {
    setEditingVoucher(voucher);
    setFormData({
      code: voucher.code,
      description: voucher.description,
      discountType: voucher.discountType,
      discountValue: voucher.discountValue,
      minPurchase: voucher.minPurchase,
      maxDiscount: voucher.maxDiscount || "",
      usageLimit: voucher.usageLimit,
      startDate: voucher.startDate,
      endDate: voucher.endDate,
      applicableRooms:
        voucher.applicableRooms === "all" ? ["all"] : (Array.isArray(voucher.applicableRooms) ? voucher.applicableRooms : []),
    });
    setShowModal(true);
  };

  // Delete voucher
  const handleDelete = async (id: string): Promise<void> => {
    const voucher = vouchers.find((v) => v._id === id);
    if (window.confirm("Bạn có chắc muốn xóa voucher này?")) {
      try {
        await vouchersAPI.delete(id);
        toast.success("Đã xóa voucher thành công");
        activityLogAPI.create({
          performedBy: user?.name || "Unknown",
          action: "DELETE_VOUCHER",
          targetType: "voucher",
          targetId: id,
          detail: `Xóa voucher: ${voucher?.code || id}`,
        }).catch(() => {});
        fetchVouchers();
      } catch (error) {
        toast.error("Lỗi khi xóa voucher");
      }
    }
  };

  // Toggle voucher status
  const toggleStatus = async (id: string): Promise<void> => {
    const voucher = vouchers.find((v) => v._id === id);
    try {
      await vouchersAPI.toggleStatus(id);
      toast.success("Cập nhật trạng thái thành công");
      activityLogAPI.create({
        performedBy: user?.name || "Unknown",
        action: "TOGGLE_VOUCHER",
        targetType: "voucher",
        targetId: id,
        detail: `Đổi trạng thái voucher: ${voucher?.code || id} (${voucher?.status === "active" ? "active → inactive" : "inactive → active"})`,
      }).catch(() => {});
      fetchVouchers();
    } catch (error) {
      toast.error("Lỗi khi cập nhật trạng thái");
    }
  };

  // Toggle room selection
  const toggleRoom = (roomId: string): void => {
    if (roomId === "all") {
      setFormData({
        ...formData,
        applicableRooms: formData.applicableRooms.includes("all")
          ? []
          : ["all"],
      });
    } else {
      let newRooms = [...formData.applicableRooms.filter((r) => r !== "all")];

      if (newRooms.includes(roomId)) {
        newRooms = newRooms.filter((r) => r !== roomId);
      } else {
        newRooms.push(roomId);
      }

      setFormData({
        ...formData,
        applicableRooms: newRooms,
      });
    }
  };

  // Save voucher
  const handleSave = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    // Convert applicableRooms
    let applicableRooms: "all" | string[] = "all";
    if (!formData.applicableRooms.includes("all")) {
      applicableRooms = formData.applicableRooms.filter((r) => r !== "all");
    }

    const saveData = {
      code: formData.code,
      description: formData.description,
      discountType: formData.discountType,
      discountValue: Number(formData.discountValue),
      minPurchase: Number(formData.minPurchase) || 0,
      maxDiscount: formData.maxDiscount ? Number(formData.maxDiscount) : null,
      usageLimit: Number(formData.usageLimit),
      startDate: formData.startDate,
      endDate: formData.endDate,
      applicableRooms,
    };

    try {
      if (editingVoucher) {
        await vouchersAPI.update(editingVoucher._id, saveData);
        toast.success("Cập nhật voucher thành công");
        activityLogAPI.create({
          performedBy: user?.name || "Unknown",
          action: "UPDATE_VOUCHER",
          targetType: "voucher",
          targetId: editingVoucher._id,
          detail: `Cập nhật voucher: ${saveData.code}`,
        }).catch(() => {});
      } else {
        await vouchersAPI.create(saveData);
        toast.success("Thêm voucher thành công");
        activityLogAPI.create({
          performedBy: user?.name || "Unknown",
          action: "CREATE_VOUCHER",
          targetType: "voucher",
          detail: `Tạo voucher mới: ${saveData.code}`,
        }).catch(() => {});
      }
      setShowModal(false);
      fetchVouchers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Có lỗi xảy ra");
    }
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  // Get room names
  const getRoomNames = (applicableRooms: "all" | string[]): string => {
    if (applicableRooms === "all") {
      return "Tất cả phòng";
    }
    if (!Array.isArray(applicableRooms) || applicableRooms.length === 0) {
      return "-";
    }
    return applicableRooms
      .map((roomId) => {
        const room = rooms.find((r) => r._id === roomId || r._id === roomId?.toString());
        return room ? room.name : roomId;
      })
      .join(", ");
  };

  // Get status badge
  const getStatusBadge = (status: string): React.ReactElement => {
    const badges: Record<string, { color: string; text: string }> = {
      active: { color: "bg-green-100 text-green-800", text: "✓ Hoạt động" },
      inactive: { color: "bg-gray-100 text-gray-800", text: "⏸ Tạm dừng" },
      expired: { color: "bg-red-100 text-red-800", text: "✕ Hết hạn" },
    };
    const badge = badges[status] || badges.inactive;
    return (
      <span
        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${badge.color}`}
      >
        {badge.text}
      </span>
    );
  };

  // Get discount text
  const getDiscountText = (voucher: Voucher): string => {
    if (voucher.discountType === "percentage") {
      return `${voucher.discountValue}%`;
    } else {
      return formatCurrency(voucher.discountValue);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 text-lg">Đang tải voucher...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Quản lý voucher</h1>
          <p className="text-gray-600 mt-1">Tạo và quản lý mã giảm giá</p>
        </div>
        <button
          onClick={handleAddNew}
          className="px-4 py-2 bg-coffee-600 text-white rounded-lg hover:bg-coffee-700 flex items-center space-x-2"
        >
          <span>+</span>
          <span>Thêm voucher</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {([["vouchers", "🎫 Danh sách voucher"], ["holders", "👥 Người dùng có voucher"]] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === key ? "border-coffee-600 text-coffee-600" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Holders Tab */}
      {activeTab === "holders" && (
        <div>
          {holdersLoading ? (
            <div className="text-center py-16 text-gray-400">Đang tải...</div>
          ) : holders.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="text-5xl mb-3">👥</div>
              <p>Chưa có người dùng nào lưu voucher</p>
            </div>
          ) : (
            <div className="space-y-3">
              {holders.map(h => (
                <div key={h.code} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  {/* Row header */}
                  <button
                    onClick={() => setExpandedCode(expandedCode === h.code ? null : h.code)}
                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-coffee-700 text-base tracking-wide">{h.code}</span>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                        {h.claimed.length} đang lưu
                      </span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                        {h.used.length} đã dùng
                      </span>
                    </div>
                    <span className="text-gray-400 text-sm">{expandedCode === h.code ? "▲" : "▼"}</span>
                  </button>

                  {expandedCode === h.code && (
                    <div className="border-t border-gray-100 divide-y divide-gray-50">
                      {/* Claimed (chưa dùng) */}
                      {h.claimed.length > 0 && (
                        <div className="px-5 py-3">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Đang lưu (chưa sử dụng)</p>
                          <div className="space-y-1.5">
                            {h.claimed.map((u, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm">
                                <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />
                                <span className="font-medium text-gray-800">{u.name}</span>
                                <span className="text-gray-400">{u.email}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Used */}
                      {h.used.length > 0 && (
                        <div className="px-5 py-3">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Đã sử dụng</p>
                          <div className="space-y-1.5">
                            {h.used.map((u, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm">
                                <span className="w-2 h-2 rounded-full bg-green-400 shrink-0" />
                                <span className="font-medium text-gray-800">{u.name}</span>
                                <span className="text-gray-400">{u.email}</span>
                                {u.checkInDate && (
                                  <span className="text-gray-400 text-xs ml-auto">{u.checkInDate} → {u.checkOutDate}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "vouchers" && <>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Tổng voucher</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {vouchers.length}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <span className="text-3xl">🎫</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Đang hoạt động</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {vouchers.filter((v) => v.status === "active").length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <span className="text-3xl">✓</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Đã sử dụng</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {vouchers.reduce((sum, v) => sum + (v.usedCount || 0), 0)}
              </p>
            </div>
            <div className="p-3 bg-coffee-100 rounded-lg">
              <span className="text-3xl">📊</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Hết hạn</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {vouchers.filter((v) => v.status === "expired").length}
              </p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <span className="text-3xl">✕</span>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm mã, mô tả voucher..."
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500 text-sm"
          />
        </div>
        <span className="text-sm text-gray-500">{filteredVouchers.length} voucher</span>
      </div>

      {/* Vouchers List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {pageVouchers.map((voucher) => (
          <div
            key={voucher._id}
            className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
          >
            {/* Voucher Header */}
            <div className="bg-gradient-to-r from-coffee-700 to-coffee-500 p-6 text-white">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-2xl font-bold">{voucher.code}</h3>
                {getStatusBadge(voucher.status)}
              </div>
              <p className="text-coffee-100">{voucher.description}</p>
            </div>

            {/* Voucher Body */}
            <div className="p-6">
              {/* Discount */}
              <div className="mb-4">
                <div className="text-3xl font-bold text-coffee-600 mb-1">
                  {getDiscountText(voucher)}
                </div>
                <p className="text-sm text-gray-600">
                  {voucher.discountType === "percentage"
                    ? "Giảm theo %"
                    : "Giảm cố định"}
                </p>
              </div>

              {/* Details */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Đơn tối thiểu:</span>
                  <span className="font-medium">
                    {formatCurrency(voucher.minPurchase)}
                  </span>
                </div>

                {voucher.maxDiscount && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Giảm tối đa:</span>
                    <span className="font-medium">
                      {formatCurrency(voucher.maxDiscount)}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Số lượng:</span>
                  <span className="font-medium">
                    {voucher.usedCount || 0}/{voucher.usageLimit}
                  </span>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Áp dụng cho:</p>
                  <p className="text-sm font-medium text-blue-600">
                    {getRoomNames(voucher.applicableRooms)}
                  </p>
                </div>

                <div className="pt-2">
                  <p className="text-xs text-gray-500">
                    {new Date(voucher.startDate).toLocaleDateString("vi-VN")}{" "}
                    - {new Date(voucher.endDate).toLocaleDateString("vi-VN")}
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                  <span>Đã dùng</span>
                  <span>
                    {voucher.usageLimit > 0
                      ? Math.round(((voucher.usedCount || 0) / voucher.usageLimit) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-coffee-600 h-2 rounded-full transition-all"
                    style={{
                      width: `${voucher.usageLimit > 0 ? ((voucher.usedCount || 0) / voucher.usageLimit) * 100 : 0}%`,
                    }}
                  ></div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button
                  onClick={() => toggleStatus(voucher._id)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium ${
                    voucher.status === "active"
                      ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                      : "bg-green-100 text-green-800 hover:bg-green-200"
                  }`}
                >
                  {voucher.status === "active" ? "Tạm dừng" : "Kích hoạt"}
                </button>
                <button
                  onClick={() => handleEdit(voucher)}
                  className="flex-1 py-2 px-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  Sửa
                </button>
                <button
                  onClick={() => handleDelete(voucher._id)}
                  className="py-2 px-3 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Empty State */}
        {filteredVouchers.length === 0 && (
          <div className="col-span-3 bg-gray-50 rounded-lg p-12 text-center">
            <div className="text-6xl mb-4">🎫</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Chưa có voucher nào
            </h3>
            <p className="text-gray-600">
              {search ? "Không tìm thấy voucher phù hợp" : "Nhấn nút \"Thêm voucher\" để tạo mã giảm giá mới"}
            </p>
          </div>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} total={filteredVouchers.length} pageSize={PAGE_SIZE} onChange={setPage} />

      </>}{/* end vouchers tab */}

      {/* Modal Add/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingVoucher ? "Chỉnh sửa voucher" : "Thêm voucher mới"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ✕
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSave} className="space-y-4">
                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mã voucher *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.code}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({
                        ...formData,
                        code: e.target.value.toUpperCase(),
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500"
                    placeholder="SUMMER2026"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mô tả *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.description}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500"
                    placeholder="Giảm giá mùa hè"
                  />
                </div>

                {/* Discount Type & Value */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Loại giảm giá *
                    </label>
                    <select
                      value={formData.discountType}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setFormData({
                          ...formData,
                          discountType: e.target.value as "percentage" | "fixed",
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500"
                    >
                      <option value="percentage">Phần trăm (%)</option>
                      <option value="fixed">Số tiền cố định (VND)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Giá trị *
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={formData.discountValue}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({
                          ...formData,
                          discountValue: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500"
                      placeholder={
                        formData.discountType === "percentage" ? "20" : "50000"
                      }
                    />
                  </div>
                </div>

                {/* Min Purchase & Max Discount */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Đơn tối thiểu (VND) *
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={formData.minPurchase}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({
                          ...formData,
                          minPurchase: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500"
                      placeholder="1000000"
                    />
                  </div>

                  {formData.discountType === "percentage" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Giảm tối đa (VND)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.maxDiscount}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFormData({
                            ...formData,
                            maxDiscount: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500"
                        placeholder="500000"
                      />
                    </div>
                  )}
                </div>

                {/* Usage Limit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Số lượng *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.usageLimit}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({ ...formData, usageLimit: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500"
                    placeholder="100"
                  />
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ngày bắt đầu *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.startDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({ ...formData, startDate: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ngày kết thúc *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.endDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({ ...formData, endDate: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500"
                    />
                  </div>
                </div>

                {/* Room Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Áp dụng cho phòng *
                  </label>

                  {/* All Rooms */}
                  <div className="mb-3">
                    <label className="flex items-center p-3 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-coffee-50">
                      <input
                        type="checkbox"
                        checked={formData.applicableRooms.includes("all")}
                        onChange={() => toggleRoom("all")}
                        className="w-4 h-4 text-coffee-600 border-gray-300 rounded focus:ring-coffee-500"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        Tất cả phòng
                      </span>
                    </label>
                  </div>

                  {/* Individual Rooms */}
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 mb-2">
                      Hoặc chọn từng phòng:
                    </p>
                    {rooms.map((room) => (
                      <label
                        key={room._id}
                        className={`flex items-center p-2 border-2 rounded-lg cursor-pointer transition-colors ${
                          formData.applicableRooms.includes(room._id)
                            ? "border-coffee-500 bg-coffee-50"
                            : "border-gray-300 hover:bg-gray-50"
                        } ${
                          formData.applicableRooms.includes("all")
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.applicableRooms.includes(room._id)}
                          onChange={() => toggleRoom(room._id)}
                          disabled={formData.applicableRooms.includes("all")}
                          className="w-4 h-4 text-coffee-600 border-gray-300 rounded focus:ring-coffee-500"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">
                          {room.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-coffee-600 text-white py-2 px-4 rounded-lg hover:bg-coffee-700 font-medium"
                  >
                    {editingVoucher ? "Cập nhật" : "Thêm voucher"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Hủy
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VouchersPage;
